// Bir sayfanın başlığını, açıklamasını, paylaşım görselini ve logosunu okumak
// (9.32 · D-653).
//
// Telefonda HTML ayrıştırıcı yok; yalnız `<head>` içindeki `<meta>`, `<link>`
// ve `<title>` etiketleri okunuyor. WhatsApp ve Telegram'ın baktığı yer de
// burası: Open Graph (`og:*`), sonra Twitter etiketleri, sonra düz başlık.

export type IconCandidate = { href: string; rel: string; size: number; type: string };

export type PageMeta = {
  title?: string;
  description?: string;
  siteName?: string;
  /** Paylaşım görseli; yalnız https. */
  image?: string;
  /**
   * Bütün görsel adayları, sırayla. İlki çalışmayabilir: YouTube canlı yayında
   * `og:image` olarak 404 dönen en büyük boyu gösteriyor (simülatörde görüldü).
   */
  images: string[];
  /** Sitenin bildirdiği oEmbed adresi (JSON); kapak için son çare. */
  oembed?: string;
  /** En uygun olandan başlayarak sıralı logo adayları; yalnız https. */
  icons: string[];
};

const NAMED: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', hellip: '…', ndash: '–', mdash: '—',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', laquo: '«', raquo: '»', copy: '©', reg: '®', trade: '™',
  middot: '·', bull: '•',
};

export function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
    }
    return NAMED[body.toLowerCase()] ?? whole;
  });
}

function attributes(tag: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
  for (let m = re.exec(tag); m; m = re.exec(tag)) {
    out[m[1].toLowerCase()] = decodeEntities(m[3] ?? m[4] ?? m[5] ?? '');
  }
  return out;
}

/**
 * Sayfadaki göreli bir adresi, sayfanın SON adresine (yönlendirmelerden sonra)
 * göre çöz. Yalnız https döner: düz http'den gelen görseli yoldaki biri
 * değiştirebilir.
 */
export function resolveHttpsUrl(href: string, base: string): string | null {
  const value = href.trim();
  if (!value) return null;
  let url: string;
  if (/^https?:\/\//i.test(value)) url = value;
  else if (value.startsWith('//')) url = `https:${value}`;
  else if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null; // data:, javascript: …
  else {
    const origin = /^(https:\/\/[^/?#]+)/i.exec(base)?.[1];
    if (!origin) return null;
    if (value.startsWith('/')) url = origin + value;
    else {
      const path = base.slice(origin.length).replace(/[?#].*$/, '');
      url = origin + path.slice(0, path.lastIndexOf('/') + 1) + value;
    }
  }
  return /^https:\/\/[^/?#\s]+/i.test(url) && !/\s/.test(url) ? url : null;
}

function iconScore(rel: string, size: number, type: string, href: string): number {
  const svg = type.includes('svg') || /\.svg(\?|#|$)/i.test(href);
  if (svg) return -1; // telefonda çizemiyoruz
  if (rel.includes('apple-touch-icon')) return 60;
  if (size >= 32 && size <= 96) return 100 - Math.abs(size - 64) / 10;
  if (size > 96) return 70;
  if (size > 0) return 40;
  return 50;
}

export function parsePageMeta(html: string, pageUrl: string): PageMeta {
  const end = html.search(/<\/head\s*>/i);
  const head = end > 0 ? html.slice(0, end) : html.slice(0, 300_000);
  const meta: Record<string, string> = {};
  for (const tag of head.match(/<meta\b[^>]*>/gi) ?? []) {
    const a = attributes(tag);
    const key = (a.property || a.name || '').toLowerCase();
    if (key && a.content !== undefined && !(key in meta)) meta[key] = a.content;
  }
  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const value = meta[key]?.replace(/\s+/g, ' ').trim();
      if (value) return value;
    }
    return undefined;
  };
  const titleTag = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(head)?.[1];
  const title = pick('og:title', 'twitter:title')
    ?? (titleTag ? decodeEntities(titleTag).replace(/\s+/g, ' ').trim() || undefined : undefined);
  const imageHref = pick('og:image:secure_url', 'og:image', 'og:image:url', 'twitter:image', 'twitter:image:src');
  const images: string[] = [];
  const addImage = (href: string | undefined) => {
    const url = href ? resolveHttpsUrl(href, pageUrl) : null;
    if (url && !images.includes(url)) images.push(url);
  };
  // Aynı ada sahip birden çok `og:image` olabilir; yalnız ilki `meta`ya girdi.
  for (const tag of head.match(/<meta\b[^>]*>/gi) ?? []) {
    const a = attributes(tag);
    const key = (a.property || a.name || '').toLowerCase();
    if (['og:image:secure_url', 'og:image', 'og:image:url', 'twitter:image', 'twitter:image:src'].includes(key)) {
      addImage(a.content);
    }
  }

  const icons: { url: string; score: number }[] = [];
  for (const tag of head.match(/<link\b[^>]*>/gi) ?? []) {
    const a = attributes(tag);
    const rel = (a.rel ?? '').toLowerCase();
    if (!/(^|\s)(icon|apple-touch-icon|apple-touch-icon-precomposed)(\s|$)/.test(rel) || !a.href) continue;
    const url = resolveHttpsUrl(a.href, pageUrl);
    if (!url) continue;
    const size = parseInt((a.sizes ?? '').split(/[x\s]/i)[0], 10) || 0;
    const score = iconScore(rel, size, (a.type ?? '').toLowerCase(), a.href);
    if (score >= 0) icons.push({ url, score });
  }
  icons.sort((x, y) => y.score - x.score);
  let oembed: string | undefined;
  for (const tag of head.match(/<link\b[^>]*>/gi) ?? []) {
    const a = attributes(tag);
    if ((a.rel ?? '').toLowerCase() === 'image_src') addImage(a.href);
    if (!oembed && (a.type ?? '').toLowerCase() === 'application/json+oembed' && a.href) {
      oembed = resolveHttpsUrl(a.href, pageUrl) ?? undefined;
    }
  }
  const ordered = icons.map((i) => i.url);
  const fallback = resolveHttpsUrl('/favicon.ico', pageUrl);
  if (fallback && !ordered.includes(fallback)) ordered.push(fallback);

  return {
    title,
    description: pick('og:description', 'twitter:description', 'description'),
    siteName: pick('og:site_name', 'application-name'),
    image: imageHref ? resolveHttpsUrl(imageHref, pageUrl) ?? undefined : undefined,
    images,
    oembed,
    icons: ordered,
  };
}
